import React, { useEffect } from 'react';
import { ViewerContext_UserFragment } from '@99designs/viewer';
import { AnnotationContext } from '../../AnnotationContext';
import { SelectedEventContext } from '../../SelectedEventContext';
import { ImageToScreenFn } from '../calculatePosition';
import Annotation from './Annotation';
import NewAnnotationForm from './NewAnnotationForm';

export interface AnnotationCanvasProps<A> {
    useSelectedEvent: SelectedEventContext['useSelectedEvent'];
    useAnnotation: AnnotationContext<A>['useAnnotation'];
    imageToScreen: ImageToScreenFn;
    viewer: ViewerContext_UserFragment;
}

function AnnotationCanvas<A>(props: AnnotationCanvasProps<A>) {
    const { useSelectedEvent, useAnnotation, imageToScreen, viewer } = props;
    const {
        annotations,
        newAnnotationCoord,
        setNewAnnotationCoord,
        addAnnotations,
        removeAnnotation,
        lastAnnotation,
        setLastAnnotation,
        createAnnotation,
        createAnnotationResult: { error },
        AnnotationDisplay,
    } = useAnnotation();
    const { selectedEventId, setSelectedEventId } = useSelectedEvent();

    useEffect(() => {
        if (error && lastAnnotation) {
            setNewAnnotationCoord(lastAnnotation.position);
            removeAnnotation(lastAnnotation.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, lastAnnotation]);

    return (
        <>
            {annotations.map((annotation) => {
                return (
                    <Annotation
                        key={annotation.id}
                        position={imageToScreen(annotation.position)}
                        focused={annotation.id === selectedEventId}
                        onClick={() => {
                            setSelectedEventId((currentAnnotation) =>
                                currentAnnotation ? null : annotation.id
                            );
                            setNewAnnotationCoord(null);
                        }}
                    >
                        <AnnotationDisplay content={annotation.content} />
                    </Annotation>
                );
            })}
            {newAnnotationCoord && (
                <Annotation focused={true} position={imageToScreen(newAnnotationCoord)}>
                    <NewAnnotationForm
                        viewer={viewer}
                        initialMessageValue={error && lastAnnotation ? lastAnnotation.content : ''}
                        error={error}
                        createAnnotation={({ message }) => {
                            const optimisticAnnotation = createAnnotation({
                                message,
                                position: newAnnotationCoord,
                                addAnnotations,
                            });

                            setSelectedEventId(optimisticAnnotation.id);
                            setNewAnnotationCoord(null);
                            setLastAnnotation(optimisticAnnotation);
                        }}
                        onCancel={() => {
                            setLastAnnotation(null);
                            setNewAnnotationCoord(null);
                        }}
                    />
                </Annotation>
            )}
        </>
    );
}

export default AnnotationCanvas;
